import React, { useState } from 'react';
import { cn } from '@/lib/utils';

// Attempt to derive a WebP/AVIF source URL for known CDNs
function getModernSrc(src) {
  if (!src) return null;

  // Supabase storage: the public `object` endpoint ignores transform query
  // parameters, so the former `format=webp&quality=80` suffix produced a
  // <source type="image/webp"> that actually served the original JPEG/PNG.
  // Serve the original directly; right-sized WebP renditions are bundled per
  // page instead (see src/assets).
  if (src.includes('supabase.co/storage')) return null;

  // Unsplash: already supports fm=webp
  if (src.includes('images.unsplash.com')) {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}fm=webp&q=80`;
  }

  // Already a modern format
  if (/\.(webp|avif)(\?|$)/i.test(src)) return null;

  return null;
}

export default function OptimizedImage({
  src,
  alt,
  className,
  width,
  height,
  fill = false,
  priority = false,
  quality = 'medium',
  objectFit = 'cover',
  objectPosition = 'center',
  sizes,
  placeholder = true,
  ...props
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  const defaultSizes =
    sizes ||
    '(max-width: 480px) 100vw, (max-width: 768px) 95vw, (max-width: 1024px) 90vw, 1280px';

  const modernSrc = getModernSrc(src);

  if (error) {
    return (
      <div className={cn('bg-slate-100 flex items-center justify-center text-xs text-slate-400', fill && 'w-full h-full', className)}>
        {alt || 'Image'}
      </div>
    );
  }

  const sharedImgProps = {
    src,
    alt: alt ?? '',
    sizes: defaultSizes,
    loading: priority ? 'eager' : 'lazy',
    decoding: priority ? 'sync' : 'async',
    fetchpriority: priority ? 'high' : 'low',
    referrerPolicy: 'no-referrer-when-downgrade',
    style: { objectFit, objectPosition, ...props.style },
    onLoad: () => setIsLoaded(true),
    onError: () => setError(true),
  };

  const imgClassName = cn(
    priority ? '' : 'transition-opacity duration-500',
    (!priority && !isLoaded) ? 'opacity-0' : 'opacity-100',
    className
  );

  const renderPicture = (imgNode) => {
    if (!modernSrc) return imgNode;
    return (
      <picture>
        <source srcSet={modernSrc} type="image/webp" sizes={defaultSizes} />
        {imgNode}
      </picture>
    );
  };

  if (!fill) {
    return renderPicture(
      <img
        {...sharedImgProps}
        width={width}
        height={height}
        className={imgClassName}
        {...props}
      />
    );
  }

  // Fill mode
  return (
    <div className="relative w-full h-full">
      {placeholder && !isLoaded && !priority && (
        <div
          className="absolute inset-0 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-50"
          aria-hidden="true"
        />
      )}
      {renderPicture(
        <img
          {...sharedImgProps}
          className={cn('w-full h-full', imgClassName)}
          {...props}
        />
      )}
    </div>
  );
}